import { ExternalLinkIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { type PendingDocumentResponse } from "@/api-v3";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { useContractLabel } from "../../hooks/use-contract-label";

interface LegalDocumentsAcknowledgementProps {
  /** Documents the participant must be shown before the action proceeds. */
  documents: PendingDocumentResponse[];
  acknowledgedIds: string[];
  onToggle: (documentId: string, acknowledged: boolean) => void;
}

/**
 * One checkbox per legal document, in display order. Every document gets its
 * own row because some programmes (Stripe's US platform) require their
 * agreements to be presented separately. Acknowledgement is recorded
 * server-side by the action that follows, never via the accept endpoint.
 */
export const LegalDocumentsAcknowledgement = ({
  documents,
  acknowledgedIds,
  onToggle,
}: LegalDocumentsAcknowledgementProps) => {
  const { t } = useTranslation();
  const contractLabel = useContractLabel();

  if (documents.length === 0) {
    return null;
  }

  const ordered = [...documents].sort((a, b) => a.order - b.order);

  return (
    <ul className="my-2 space-y-2">
      {ordered.map((document, index) => {
        const checkboxId = `accept-legal-document-${document.id}`;
        const label = contractLabel(document.title);
        const url = document.version.document_url;

        return (
          <li key={document.id}>
            <Label
              htmlFor={checkboxId}
              className="inline-flex items-center cursor-pointer align-middle py-2"
            >
              <Checkbox
                id={checkboxId}
                name={checkboxId}
                // Kept for the e2e page object, which targets the first row.
                data-test-id={
                  index === 0 ? "accept-card-agreement-checkbox" : undefined
                }
                checked={acknowledgedIds.includes(document.id)}
                onCheckedChange={(checked) =>
                  onToggle(document.id, Boolean(checked))
                }
              />
              <span className="ml-3">
                {t("accept")}{" "}
                {url ? (
                  <a
                    href={url}
                    className="inline-flex items-center gap-x-2 text-primary hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {label} <ExternalLinkIcon className="w-4 h-4" />
                  </a>
                ) : (
                  label
                )}
                .
              </span>
            </Label>
          </li>
        );
      })}
    </ul>
  );
};
